import { Deal } from "@ifgengineering/client-invest-sdk";

// Local mirror of the SDK fundraising-capacity types; the installed SDK omits windowStart.
export type CapacityState = "OPEN" | "LIMITED" | "FULL";

export interface DealFundraisingCapacity {
  fundraisingCap: number | null;
  remainingCapacity: number | null;
  capacityState: CapacityState | null;
  /** Active round's start (openedAt); null when there is no active round. */
  windowStart: string | null;
}

export type DealsFundraisingCapacity = Record<string, DealFundraisingCapacity>;

// SDK Deal type predates usesFundraisingRounds; read it via a narrow cast.
export const dealUsesFundraisingRounds = (deal: Deal): boolean =>
  (deal as Deal & { usesFundraisingRounds?: boolean }).usesFundraisingRounds ??
  false;

// Rounds-enforced deal with no active round (windowStart null) is closed to new money.
export const isFundraisingRoundClosed = (
  deal: Deal | null | undefined,
  capacity: DealFundraisingCapacity | null | undefined
): boolean =>
  !!deal &&
  dealUsesFundraisingRounds(deal) &&
  capacity != null &&
  capacity.windowStart == null;

// The gauge (progress bar + readout) shows only once the round is at least this subscribed.
export const MIN_VISIBLE_RAISED_RATIO = 0.5;

// True once the round is at least MIN_VISIBLE_RAISED_RATIO subscribed; below that the
// gauge is hidden while the fundraising limit stays visible.
export const isGaugeVisible = (
  capacity: DealFundraisingCapacity | null | undefined
): boolean => {
  if (capacity == null || capacity.fundraisingCap == null) return false;
  const cap = capacity.fundraisingCap;
  if (cap <= 0) return false;
  const raised = Math.max(cap - (capacity.remainingCapacity ?? 0), 0);
  return raised / cap >= MIN_VISIBLE_RAISED_RATIO;
};
